import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store";

export type FileUploadStatus =
  | "uploading"
  | "scanning"
  | "completed"
  | "malicious"
  | "error";

export interface FileUploadState {
  status: FileUploadStatus;
  id: string | null;
  name: string;
  isMalicious: boolean | null;
  error: string | null;
}

export interface FieldUploadsState {
  uploads: Record<string, FileUploadState[]>; // key is field name, each field can have multiple uploads
}

const initialState: FieldUploadsState = {
  uploads: {},
};

interface FileUploadStartedPayload {
  fieldName: string;
  fileName: string;
}

interface FileScanStartedPayload {
  fieldName: string;
  fileName: string;
  fileId: string;
}

interface FileScanCompletedPayload {
  fileId: string;
  isMalicious: boolean;
}

interface FileUploadFailedPayload {
  fieldName: string;
  fileName: string;
}

interface RemoveFileUploadPayload {
  fieldName: string;
  fileName: string;
}

export const fileUploadSlice = createSlice({
  name: "fileUpload",
  initialState,
  reducers: {    
    fileUploadStarted: (state, action: PayloadAction<FileUploadStartedPayload>) => {
      const { fieldName, fileName } = action.payload;

      if (!state.uploads[fieldName]) {
        state.uploads[fieldName] = [];
      }

      if (state.uploads[fieldName]?.some(upload => upload.name === fileName)) {
        return;
      }

      state.uploads[fieldName].push({
        status: "uploading",
        id: null,
        name: fileName,
        isMalicious: null,
        error: null,
      });
    },
    fileScanStarted: (state, action: PayloadAction<FileScanStartedPayload>) => {
      const { fieldName, fileName, fileId } = action.payload;

      const upload = state.uploads[fieldName]?.find(upload => upload.name === fileName);

      if (upload) {
        upload.id = fileId;
        upload.status = "scanning";
      }
    },
    fileScanComplete: (state, action: PayloadAction<FileScanCompletedPayload>) => {
      const { fileId, isMalicious } = action.payload;

      const upload = Object.values(state.uploads).flat().find(upload => upload.id === fileId);

      if (upload) {
        upload.status = isMalicious ? "malicious" : "completed";
        upload.isMalicious = isMalicious;
        upload.error = isMalicious ? `File '${upload.name}' has been flag as malicious and cannot be used.` : null;
      }
    },
    fileUploadFailed: (state, action: PayloadAction<FileUploadFailedPayload>) => {
      const { fieldName, fileName } = action.payload;
      const upload = state.uploads[fieldName]?.find(upload => upload.name === fileName);
      if (upload) {
        upload.isMalicious = null;
        upload.status = "error";
        upload.error = `Failed to upload file '${fileName}'.`;
      }
    },
    removeFileUpload: (state, action: PayloadAction<RemoveFileUploadPayload>) => {
      const { fieldName, fileName } = action.payload;
      const uploads = state.uploads[fieldName];
      if (uploads) {
        state.uploads[fieldName] = uploads.filter(upload => upload.name !== fileName);
      }
    },
    clearFileUploads: (state) => {
      state.uploads = {};
    },
  },
});

export const {
  fileUploadStarted,
  fileScanStarted,
  fileScanComplete,
  fileUploadFailed,
  removeFileUpload,
  clearFileUploads,
} = fileUploadSlice.actions;

export const selectAreFileUploadsInProgress = (state: RootState) => 
  Object.values(state.fileUpload.uploads).flat().some(
    (upload) => upload.status === "uploading" || upload.status === "scanning"
  );

export const selectAreFileUploadsComplete = (state: RootState) => 
  Object.values(state.fileUpload.uploads).flat().every(
    (upload) => upload.status === "completed"
  );

export const selectFileUploads = (state: RootState) => 
  state.fileUpload.uploads || [];

export const selectFieldFileUploads = (state: RootState, fieldName: string) => 
  state.fileUpload.uploads[fieldName] || [];

export const fileUploadReducer = fileUploadSlice.reducer;
